import { useEffect, useMemo, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AppHeader } from './AppHeader';
import { AppSideNav } from './AppSideNav';
import { AppMobileNav } from './AppMobileNav';
import { StyledAppLayout } from './AppLayout.styles';
import { MachineDetailsDrawer } from 'app/cross-cutting-concerns/drawers/components/MachineDetailsDrawer/MachineDetailsDrawer';
import { SiteDetailsDrawer } from 'app/cross-cutting-concerns/drawers/components/SiteDetailsDrawer/SiteDetailsDrawer';
import { ScrollToTop } from 'lib/components/ScrollToTop/ScrollToTop';
import { VncV2 } from 'app/components/Vnc/components/VncV2';
import { VncSelectors } from 'app/components/Vnc/state/vncSelectors';
import {
  selectIsCheckingAuthentication,
  selectIsLoggedIn,
} from 'app/cross-cutting-concerns/authentication/state/authenticationSelectors';
import {
  LOCAL_STORAGE_VNC_KEY,
  LOCAL_STORAGE_VNC_ROBOT_SOFTWARE_VERSION_KEY,
  LOCAL_STORAGE_VNC_SERIAL_NUMBER,
} from 'config/constants';

const SIDENAV_COLLAPSING_STATE_KEY = 'sidenav-collapsing-state';

const getStoredSiderCollapsed = (): boolean => {
  try {
    return localStorage.getItem(SIDENAV_COLLAPSING_STATE_KEY) === 'true';
  } catch {
    return false;
  }
};

export const AppLayout = (): JSX.Element => {
  const [siderCollapsed, setSiderCollapsed] = useState(getStoredSiderCollapsed);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleToggleSider = (): void => {
    setSiderCollapsed(prev => {
      const next = !prev;

      try {
        localStorage.setItem(SIDENAV_COLLAPSING_STATE_KEY, String(next));
      } catch {
        // ignore storage write failures (e.g. private browsing)
      }

      return next;
    });
  };

  const machineIdLocalStored = localStorage.getItem(LOCAL_STORAGE_VNC_KEY);

  const isVncWindowOpen = useSelector(VncSelectors.selectVncWindowIsOpen);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const isCheckingAuthentication = useSelector(selectIsCheckingAuthentication);

  const isVncComponentAvailable = useMemo(
    () => (machineIdLocalStored || isVncWindowOpen) && isLoggedIn,
    [machineIdLocalStored, isVncWindowOpen, isLoggedIn]
  );

  useEffect(() => {
    if (!isLoggedIn && !isCheckingAuthentication) {
      localStorage.removeItem(LOCAL_STORAGE_VNC_KEY);
      localStorage.removeItem(LOCAL_STORAGE_VNC_SERIAL_NUMBER);
      localStorage.removeItem(LOCAL_STORAGE_VNC_ROBOT_SOFTWARE_VERSION_KEY);
    }
  }, [isLoggedIn, isCheckingAuthentication]);

  return (
    <StyledAppLayout>
      <AppHeader
        collapsed={siderCollapsed}
        onToggleSider={handleToggleSider}
        onOpenMobileNav={(): void => setMobileNavOpen(true)}
      />
      <div className="app-layout__body">
        <AppSideNav collapsed={siderCollapsed} />
        <main className="app-layout__content" ref={contentRef}>
          <ScrollToTop containerRef={contentRef} />
          <Outlet />
          {isVncComponentAvailable ? <VncV2 /> : null}
          <MachineDetailsDrawer />
          <SiteDetailsDrawer />
        </main>
      </div>
      <AppMobileNav open={mobileNavOpen} onClose={(): void => setMobileNavOpen(false)} />
    </StyledAppLayout>
  );
};
